import { NotFoundLayout } from "@/components/templates";
import { OnboardingLayout } from "@/components/templates";

import { UserRole } from "@/constants/CRoles";

import { TRoute } from "@/types";

import {
	Auth,
	ForgotPassword,
	ResetPassword,
	VerifyEmail,
	SubscriptionSuccess,
	ViewDetails,
	ViewsList,
	ClientDetails,
	CompanyDetails,
	Onboarding,
	ProjectDetails,
	Projects,
	Register,
	SSOCallback,
} from "@/views";
import { Admin } from "@/views/Admin/Admin";
import { AdminWorkspaceDetail } from "@/views/Admin/AdminWorkspaceDetail";
import { Home } from "@/views/Home/Home";
import { InvoiceEditView } from "@/views/Invoices/InvoiceEdit";
import { InvoicesView } from "@/views/Invoices/Invoices";
import { Me } from "@/views/Me/Me";
import { ReportEditView } from "@/views/Reports/ReportEdit";
import { ReportsView } from "@/views/Reports/Reports";

import { NavBarRoutes } from "./NavRoutes";
import { ProfileMenuRoutesBase } from "./ProfileRoutes";
import { ViewCreateRedirect, TaskRedirect } from "./redirects";

export const PublicRoutes: TRoute[] = [
	{
		name: "Login",
		path: "/login",
		element: Auth,
		guestOnly: true,
	},
	{
		name: "Register",
		path: "/register",
		element: Register,
		guestOnly: true,
	},
	{
		name: "SSO Callback",
		path: "/auth/sso/callback",
		element: SSOCallback,
	},
	{
		name: "Forgot password",
		path: "/forgot-password",
		element: ForgotPassword,
		guestOnly: true,
	},
	{
		// Not guest-only: a signed-in user may reset from a mail too.
		name: "Reset password",
		path: "/reset-password",
		element: ResetPassword,
	},
	{
		// Signed in or not: the link is opened from a mailbox.
		name: "Verify email",
		path: "/verify-email",
		element: VerifyEmail,
	},
];

// No side menu of their own any more: reached from the sidebar's
// profile link and from each other.
export const ProfileMenuRoutes: TRoute[] = ProfileMenuRoutesBase.map(
	(route) => ({ ...route, picto: route.picto ?? "menu" })
);

export const PrivateRoutes: TRoute[] = [
	{
		name: "View",
		// The home page, as its URL describes it (see UHomePath).
		path: ["/", "/view/:viewId", "/view/:viewId/tasks/:taskId"],
		element: Home,
	},
	{
		name: "Task",
		path: ["/tasks/:taskId"],
		element: TaskRedirect,
		menu: false,
	},
	{
		name: "Views",
		path: ["/views"],
		element: ViewsList,
	},
	{
		name: "View Details",
		path: ["/views/:id"],
		element: ViewDetails,
		menu: false,
	},
	{
		name: "Projects",
		path: ["/projects"],
		element: Projects,
	},
	{
		name: "Project",
		path: ["/projects/:id"],
		element: ProjectDetails,
		menu: false,
	},
	{
		name: "Onboarding",
		path: ["/onboarding"],
		layout: OnboardingLayout,
		element: Onboarding,
	},
	{
		name: "Add View",
		path: ["/view/create"],
		element: ViewCreateRedirect,
		menu: false,
	},
	{
		name: "Reports",
		path: ["/reports"],
		element: ReportsView,
	},
	{
		name: "Report New",
		path: ["/reports/new"],
		element: ReportEditView,
	},
	{
		name: "Report Edit",
		path: ["/reports/:id"],
		element: ReportEditView,
	},
	{
		name: "Invoices",
		path: ["/invoices"],
		element: InvoicesView,
		feature: "invoices",
	},
	{
		name: "Invoice New",
		path: ["/invoices/new"],
		element: InvoiceEditView,
		feature: "invoices",
	},
	{
		name: "Invoice Edit",
		path: ["/invoices/:id"],
		element: InvoiceEditView,
		feature: "invoices",
	},
	{
		name: "Company",
		path: ["/companies/:id"],
		element: CompanyDetails,
		menu: false,
	},
	{
		name: "Client",
		path: ["/clients/:id"],
		element: ClientDetails,
		menu: false,
	},
	{
		name: "Subscription Success",
		path: ["/subscription/success"],
		element: SubscriptionSuccess,
		menu: false,
	},
	{
		name: "Admin User",
		path: ["/admin/workspaces/:id"],
		element: AdminWorkspaceDetail,
		roles: [UserRole.ADMIN],
		menu: false,
	},
	{
		name: "Admin",
		path: ["/admin", "/admin/:tab"],
		element: Admin,
		// The one role gate left: admin pages, not a plan feature.
		roles: [UserRole.ADMIN],
		menu: false,
	},
	{
		// The mobile "Me" tab; the desktop sidebar holds the same links.
		name: "Me",
		path: ["/me"],
		element: Me,
		menu: false,
	},
	{
		name: "404",
		path: ["*"],
		element: NotFoundLayout,
		menu: false,
	},
	...ProfileMenuRoutes,
	...NavBarRoutes,
];
